import React, { useCallback, useEffect, useState } from "react";
import { IoMdAdd } from "react-icons/io";
import { staffApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Button, Badge, Table, Modal, Field } from "../../Components/UI/ui";
import { GATE, ROLES, fullName, cap, canManageRole, grantableRoles } from "../../lib/lab";
import { AddMemberModal, AddExistingModal, EditMemberModal, TempPasswordModal, SignatureUpload } from "./MemberForms";

// Settings → Members (frontend guide §2). Owner/admin manage within the rank
// rule; everyone else sees a read-only directory.
const Members = () => {
  const { user, labRole, can } = useAuth();
  const { toast } = useApp();
  const manager = can(...GATE.manageMembers);
  const roles = grantableRoles(labRole);
  const myStaffId = user?.staff?._id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("");
  const [active, setActive] = useState("true");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [existingOpen, setExistingOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [temp, setTemp] = useState(null);           // { member, password }
  const [roleFor, setRoleFor] = useState(null);     // member whose role is being changed
  const [newRole, setNewRole] = useState("");
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [sigFor, setSigFor] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (role) params.role = role;
      if (active) params.isActive = active;
      if (search.trim()) params.search = search.trim();
      const res = await staffApi.list(params);
      setRows(res?.data || []);
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [role, active, search, toast]);

  useEffect(() => {
    const h = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, search]);

  const patch = (m) => setRows((xs) => xs.map((x) => (x._id === m._id ? { ...x, ...m } : x)));
  const manageable = (m) => manager && canManageRole(labRole, m.role) && m._id !== myStaffId;

  const changeRole = async () => {
    setBusy("role");
    try {
      const m = await staffApi.setRole(roleFor.staffId || roleFor._id, newRole);
      patch(m?.role ? m : { ...roleFor, role: newRole });
      toast.success(`${fullName(roleFor.name)} is now ${cap(newRole)}`);
      setRoleFor(null);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const toggleActive = async (m) => {
    const next = !m.isActive;
    if (!next && !window.confirm(`Deactivate ${fullName(m.name)}? They can no longer log in to this lab (other labs are unaffected).`)) return;
    setBusy(m._id);
    try {
      const r = await staffApi.setActive(m.staffId || m._id, next);
      patch(r?._id ? r : { ...m, isActive: next });
      toast.success(next ? "Reactivated" : "Deactivated");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const doReset = async () => {
    if (resetPw.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy("reset");
    try {
      await staffApi.resetPassword(resetFor.staffId || resetFor._id, resetPw);
      patch({ ...resetFor, login: { ...(resetFor.login || {}), mustChangePassword: true } });
      setTemp({ member: resetFor, password: resetPw });
      setResetFor(null); setResetPw("");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${fullName(m.name)} from this lab?\n\nThey keep access to any other labs, and results they entered or verified stay attributed to them.`)) return;
    setBusy(m._id);
    try {
      await staffApi.remove(m.staffId || m._id);
      setRows((xs) => xs.filter((x) => x._id !== m._id));
      toast.success("Removed from lab");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const columns = [
    { key: "name", title: "Member", render: (m) => (<><div className="primary">{fullName(m.name)}{m._id === myStaffId && <span className="lab-muted"> (you)</span>}</div><div className="sub">{cap(m.staffType) || "—"}{m.qualification ? ` · ${m.qualification}` : ""}{m.registrationNumber ? ` · Reg. ${m.registrationNumber}` : ""}</div></>) },
    { key: "role", title: "Role", render: (m) => <Badge kind="role">{cap(m.role)}</Badge> },
    { key: "contact", title: "Contact", render: (m) => (<><div>{m.contact?.phoneNumber?.phoneNumber || "—"}</div><div className="sub">{m.contact?.email || ""}</div></>) },
    { key: "login", title: "Login", render: (m) => (<><div>{m.login?.username || <span className="sub">no login</span>}</div><div className="lab-row" style={{ gap: 4 }}>{m.login?.mustChangePassword && <Badge kind="IN_PROCESS">Must change password</Badge>}{m.login?.accountActive === false && <Badge kind="off">Account disabled</Badge>}</div></>) },
    { key: "isActive", title: "Access", render: (m) => <Badge kind={m.isActive ? "on" : "off"}>{m.isActive ? "Active" : "Inactive"}</Badge> },
    ...(manager ? [{ key: "actions", title: "", render: (m) => manageable(m) ? (
      <div className="lab-row end" style={{ gap: 4 }}>
        <Button size="xs" onClick={() => setEditing(m)}>Edit</Button>
        <Button size="xs" onClick={() => { setRoleFor(m); setNewRole(m.role); }}>Role</Button>
        <Button size="xs" loading={busy === m._id} onClick={() => toggleActive(m)}>{m.isActive ? "Deactivate" : "Activate"}</Button>
        <Button size="xs" onClick={() => { setResetFor(m); setResetPw(""); }}>Reset password</Button>
        <Button size="xs" variant="ghost" onClick={() => setSigFor(m)}>Signature</Button>
        {labRole === "owner" && <Button size="xs" variant="danger" onClick={() => remove(m)}>Remove</Button>}
      </div>
    ) : <span className="sub">{m._id === myStaffId ? "Edit under My profile" : ""}</span> }] : []),
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="lab-toolbar">
        <input className="lab-input grow" placeholder="Search name, username or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="lab-select" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{cap(r)}</option>)}
        </select>
        <select className="lab-select" value={active} onChange={(e) => setActive(e.target.value)}>
          <option value="true">Active</option><option value="false">Inactive</option><option value="">All</option>
        </select>
        {manager && (
          <>
            <Button onClick={() => setExistingOpen(true)}>Add existing user</Button>
            <Button variant="primary" onClick={() => setAddOpen(true)}><IoMdAdd /> Add member</Button>
          </>
        )}
      </div>
      <Table columns={columns} rows={rows} loading={loading} empty="No members match" />
      {manager && labRole === "admin" && <span className="lab-muted">As an admin you can manage signatories, staff and front desk. Owners manage admins.</span>}

      <AddMemberModal open={addOpen} onClose={() => setAddOpen(false)} roles={roles} onCreated={(m, pw) => { setRows((xs) => [m, ...xs]); setTemp({ member: m, password: pw }); }} />
      <AddExistingModal open={existingOpen} onClose={() => setExistingOpen(false)} roles={roles} onAdded={(m) => { setRows((xs) => [m, ...xs]); toast.success(`${fullName(m.name)} added`); }} />
      <EditMemberModal open={!!editing} onClose={() => setEditing(null)} member={editing} onSaved={(m) => patch(m?._id ? m : { ...editing })} />
      <TempPasswordModal open={!!temp} onClose={() => setTemp(null)} member={temp?.member} password={temp?.password} />

      <Modal open={!!roleFor} onClose={() => setRoleFor(null)} size="sm" title={`Change role · ${roleFor ? fullName(roleFor.name) : ""}`}
        footer={<><Button onClick={() => setRoleFor(null)}>Cancel</Button><Button variant="primary" loading={busy === "role"} disabled={newRole === roleFor?.role} onClick={changeRole}>Change</Button></>}>
        <Field label="New role">
          <select className="lab-select w-full" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
            {roles.map((r) => <option key={r} value={r}>{cap(r)}</option>)}
          </select>
        </Field>
        {newRole === "signatory" && roleFor && (!roleFor.qualification || !roleFor.registrationNumber) && (
          <span className="lab-danger-text">Add their qualification and registration number (Edit) — both print on reports they sign.</span>
        )}
      </Modal>

      <Modal open={!!resetFor} onClose={() => setResetFor(null)} size="sm" title={`Reset password · ${resetFor ? fullName(resetFor.name) : ""}`}
        footer={<><Button onClick={() => setResetFor(null)}>Cancel</Button><Button variant="primary" loading={busy === "reset"} onClick={doReset}>Reset</Button></>}>
        <Field label="Temporary password" hint="At least 6 characters. They'll be forced to change it at next login.">
          <input className="lab-input w-full" autoComplete="new-password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} autoFocus />
        </Field>
      </Modal>

      <Modal open={!!sigFor} onClose={() => setSigFor(null)} title={`Signature · ${sigFor ? fullName(sigFor.name) : ""}`} footer={<Button onClick={() => setSigFor(null)}>Close</Button>}>
        {sigFor && <SignatureUpload staffId={sigFor.staffId || sigFor._id} hasSignature={!!sigFor.signatureImageId} onUploaded={() => patch({ ...sigFor, signatureImageId: sigFor.signatureImageId || "new" })} />}
      </Modal>
    </div>
  );
};

export default Members;
